/**
 * pages.js — form states for the static info pages slice
 * (about / contact / faq / wholesale).
 *
 * Contracts:
 * - Discount-code copy button: any [data-newsletter-copy] copies WELCOME10
 *   and swaps [data-newsletter-copy-hint] to data-copied-text for 2s
 *   (shown in the native {% form 'customer' %} success state on the about page).
 * - FAQ filter (port of faq-client.tsx category pills): inside [data-faq],
 *   [data-faq-filter="<cat>"] buttons show/hide [data-faq-group="<cat>"]
 *   groups ("all" shows everything).
 * - Native contact/wholesale forms: submit buttons with [data-sending-text]
 *   swap their [data-submit-label] to the sending text (and disable) while
 *   the page-reload submission is in flight.
 */
(function () {
  "use strict";


  /* ---------------------------------------------------------------- *
   * WELCOME10 copy button (JS + native fallback success cards)
   * ---------------------------------------------------------------- */
  function initCopyButton(button) {
    if (button.__pagesCopy) return;
    button.__pagesCopy = true;

    button.addEventListener("click", () => {
      const hint = button.querySelector("[data-newsletter-copy-hint]");
      const copiedText = button.getAttribute("data-copied-text") || "";

      const markCopied = () => {
        if (!hint || !copiedText) return;
        if (button.__copyTimer) clearTimeout(button.__copyTimer);
        if (typeof button.__hintDefault === "undefined") {
          button.__hintDefault = hint.textContent;
        }
        hint.textContent = copiedText;
        button.__copyTimer = setTimeout(() => {
          hint.textContent = button.__hintDefault;
        }, 2000);
      };

      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText("WELCOME10").then(markCopied, () => {
          fallbackCopy();
          markCopied();
        });
      } else {
        fallbackCopy();
        markCopied();
      }

      function fallbackCopy() {
        const textArea = document.createElement("textarea");
        textArea.value = "WELCOME10";
        document.body.appendChild(textArea);
        textArea.select();
        try {
          document.execCommand("copy");
        } catch (e) {
          /* noop */
        }
        document.body.removeChild(textArea);
      }
    });
  }

  /* ---------------------------------------------------------------- *
   * FAQ category filter pills
   * ---------------------------------------------------------------- */
  const PILL_ACTIVE = ["bg-primary", "text-white", "shadow-lg"];
  const PILL_INACTIVE = [
    "bg-white/60",
    "border",
    "border-primary/20",
    "text-tertiary",
    "hover:bg-primary/10",
    "hover:border-primary/30",
  ];

  function initFaq(root) {
    if (root.__pagesFaq) return;
    root.__pagesFaq = true;

    const pills = Array.from(root.querySelectorAll("[data-faq-filter]"));
    const groups = Array.from(root.querySelectorAll("[data-faq-group]"));
    if (!pills.length || !groups.length) return;

    pills.forEach((pill) => {
      pill.addEventListener("click", () => {
        const selected = pill.getAttribute("data-faq-filter");

        pills.forEach((p) => {
          const isActive = p === pill;
          p.setAttribute("aria-pressed", isActive ? "true" : "false");
          PILL_ACTIVE.forEach((c) => p.classList.toggle(c, isActive));
          PILL_INACTIVE.forEach((c) => p.classList.toggle(c, !isActive));
        });

        groups.forEach((group) => {
          const match =
            selected === "all" ||
            group.getAttribute("data-faq-group") === selected;
          group.hidden = !match;
          // Re-trigger the scroll-in animation state for revealed groups.
          if (match) group.classList.add("visible");
        });
      });
    });
  }

  /* ---------------------------------------------------------------- *
   * Native contact/wholesale forms — "Sending…" state
   * ---------------------------------------------------------------- */
  function initSendingState(button) {
    if (button.__pagesSending) return;
    button.__pagesSending = true;

    const form = button.closest("form");
    if (!form) return;

    form.addEventListener("submit", () => {
      const sendingText = button.getAttribute("data-sending-text");
      if (!sendingText) return;
      const label = button.querySelector("[data-submit-label]");
      const icon = button.querySelector("[data-submit-icon]");
      if (label) label.textContent = sendingText;
      else button.textContent = sendingText;
      if (icon) icon.style.display = "none";
      button.setAttribute("aria-busy", "true");
      // Disable after the submit event has been processed so the
      // submission itself is not cancelled.
      setTimeout(() => {
        button.disabled = true;
      }, 0);
    });
  }

  function scan(root) {
    const scope = root || document;
    scope.querySelectorAll("[data-newsletter-copy]").forEach(initCopyButton);
    scope.querySelectorAll("[data-faq]").forEach(initFaq);
    scope
      .querySelectorAll('form button[type="submit"][data-sending-text]')
      .forEach(initSendingState);
  }

  if (window.themeUtils && window.themeUtils.onReady) {
    window.themeUtils.onReady(() => scan());
  } else if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => scan());
  } else {
    scan();
  }

  window.themePages = { scan: scan };
})();
